import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import {
  X, ChevronRight, Search, Mic, Copy, FileText, User, Calendar,
  AlertTriangle, Archive, Save, Lock, ChevronLeft, ChevronDown, Plus, Pencil, Eye,
  Pin, Download, Mail, Send, CheckCircle2
} from "lucide-react";
import BodyChartEditor, { BodyChartPreview, BodyChartLightbox, getTemplateImage } from "@/components/BodyChartEditor";
import { useAuth } from "@/contexts/AuthContext";
import { generateNotePdf } from "@/lib/pdfSystem";
import { CopyButton } from "@/components/ui/copy-button";
import { noteDisplayFields } from "@/lib/noteFields";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

// ══════════════════════════════════════════════════════════════════════════════
// FIELD KEY ARCHITECTURE - Critical for avoiding field duplication bug
// ══════════════════════════════════════════════════════════════════════════════
// 
// Problem: Templates can have multiple sections with the same semantic_type
// (e.g., two "free_text" fields). Using semantic_type as the form state key
// caused all fields with the same semantic_type to share state.
//
// Solution:
// - Form state is ALWAYS keyed by section.id (unique per section)
// - Templates are normalized on load to ensure every section has a unique id
// - AI output (keyed by semantic_type) is mapped to section IDs via the template
// - Saved notes store values keyed by section.id
// - Legacy notes (keyed by semantic_type) are migrated on load
//
// ══════════════════════════════════════════════════════════════════════════════

// Semantic type to legacy field key mapping (for backwards compatibility with old notes)
const SEMANTIC_TO_LEGACY_FIELD = {
  current_primary_issue: "presenting_complaint",
  symptom_progression: "history",
  trauma_history: "history",
  surgery_history: "medical_history",
  medical_conditions: "medical_history",
  medication: "medication",
  systems_review: "medical_history",
  examination_findings: "examination",
  treatment_given: "treatment",
  follow_up_plan: "follow_up",
  clinician_notes: "clinician_notes",
  free_text: "clinician_notes",
  // Additional mappings for full coverage
  advice: "advice",
  outcome: "outcome",
  progression: "history",
  treatment_plan: "follow_up",
};

// Generate a unique ID for sections that don't have one
const generateSectionId = () => `sec_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Normalize template sections to ensure every section has a unique ID
// Also validates there are no duplicate IDs within the template
const normalizeTemplateSections = (sections) => {
  if (!sections || !Array.isArray(sections)) return [];
  
  const seenIds = new Set();
  return sections.map((section, index) => {
    let id = section.id;
    
    // If no ID or duplicate ID, generate a new unique one
    if (!id || seenIds.has(id)) {
      id = generateSectionId();
    }
    
    seenIds.add(id);
    return { ...section, id };
  });
};

// Build a mapping from semantic_type to section IDs for AI note application
// Returns: { semantic_type: [sectionId1, sectionId2, ...] }
const buildSemanticToIdMap = (sections) => {
  const map = {};
  for (const section of sections) {
    const semType = section.semantic_type || "free_text";
    if (!map[semType]) map[semType] = [];
    map[semType].push(section.id);
  }
  return map;
};

// Initialize form state from an existing note, mapping old keys to new section IDs
const initializeFormState = (existingNote, templateSections) => {
  if (!existingNote) return { appointment_type: "", status: "draft" };
  
  const baseForm = { 
    ...existingNote, 
    appointment_type: existingNote.appointment_type || "",
    status: existingNote.status || "draft"
  };
  
  const savedValues = existingNote.field_values || {};

  // If we have template sections, map saved values onto section IDs
  if (templateSections && templateSections.length > 0) {
    for (const section of templateSections) {
      const sectionId = section.id;
      const semType = section.semantic_type;
      const legacyKey = SEMANTIC_TO_LEGACY_FIELD[semType] || semType;
      
      // Priority: 1) saved field_values by field id, 2) value keyed by section.id, 3) semantic_type, 4) legacy field
      if (savedValues[sectionId] !== undefined) {
        baseForm[sectionId] = savedValues[sectionId];
      } else if (existingNote[sectionId] !== undefined) {
        // Already has value for this section ID
        baseForm[sectionId] = existingNote[sectionId];
      } else if (existingNote[semType] !== undefined) {
        // Try semantic_type key (old format)
        baseForm[sectionId] = existingNote[semType];
      } else if (existingNote[legacyKey] !== undefined) {
        // Try legacy field key
        baseForm[sectionId] = existingNote[legacyKey];
      }
    }
  }
  
  return baseForm;
};

// Supporting AI transcript kept with a saved note (draft, or final until retention expires)
export const SavedTranscriptPanel = ({ note, transcript, diarizedSegments = [] }) => {
  const [open, setOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const deleted = !transcript && note?.ai_transcript_deleted_at;
  if (!transcript && !deleted) return null;
  const meta = note?.ai_meta || {};

  return (
    <div className="mb-5 bg-[#FAF9F6] border border-[#1A1B1A]/[0.06] rounded-xl p-4" data-testid="saved-transcript-panel">
      <div className="flex items-center gap-2">
        <FileText className="w-3.5 h-3.5 text-[#496275]" />
        <p className="text-[11px] font-medium text-[#1A1B1A]">Supporting AI transcript</p>
      </div>
      {deleted ? (
        <p className="text-[10px] text-[#757675] mt-1.5" data-testid="transcript-deleted-message">
          Transcript deleted according to clinic retention policy.
        </p>
      ) : (
        <>
          <p className="text-[10px] text-[#757675] mt-1">
            Kept with this note to support your review. {note?.status === "final" ? "Removed after the clinic retention period." : "Available while the note is a draft."}
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <button onClick={() => setOpen(!open)} data-testid="saved-view-transcript-btn"
              className="text-[10px] font-medium text-[#205B5A] bg-white border border-[#205B5A]/20 px-3 py-1 rounded-full hover:bg-[#205B5A]/5 flex items-center gap-1">
              <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
              {open ? "Hide transcript" : "View transcript"}
            </button>
            <CopyButton value={transcript} label="Copy transcript" toastMessage="Transcript copied" variant="button" size="xs">
              Copy transcript
            </CopyButton>
          </div>
          {open && (
            <div className="mt-2 bg-white rounded-lg border border-[#1A1B1A]/[0.06] p-3 max-h-64 overflow-y-auto" data-testid="saved-transcript-viewer">
              {diarizedSegments.length > 0 ? (
                <div className="space-y-2">
                  {diarizedSegments.map((seg, i) => (
                    <div key={i} className="flex gap-2">
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${seg.speaker === "A" || seg.speaker === "Patient" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"}`}>
                        {seg.speaker === "A" ? "Patient" : seg.speaker === "B" ? "Practitioner" : seg.speaker}
                      </span>
                      <p className="text-[10px] text-[#4A4B4A] leading-relaxed">{seg.text}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">{transcript}</p>
              )}
            </div>
          )}
        </>
      )}
      <button onClick={() => setShowDetails(!showDetails)} data-testid="saved-ai-details-toggle"
        className="mt-2 text-[10px] text-[#757675] hover:text-[#4A4B4A] flex items-center gap-1">
        <ChevronDown className={`w-3 h-3 transition-transform ${showDetails ? "rotate-180" : ""}`} />
        AI processing details
      </button>
      {showDetails && (
        <div className="mt-1.5 bg-white/70 rounded-md border border-[#1A1B1A]/[0.06] p-2 space-y-1" data-testid="saved-ai-details-panel">
          {meta.detail_profile && <p className="text-[9px] text-[#4A4B4A]">Consultation length profile: {meta.detail_profile}</p>}
          {meta.passes && <p className="text-[9px] text-[#4A4B4A]">Generation passes: {meta.passes}</p>}
          {meta.fields_left_blank?.length > 0 && <p className="text-[9px] text-[#4A4B4A]">Left blank at generation: {meta.fields_left_blank.join(", ")}</p>}
          {meta.internal_notes?.length > 0 && <p className="text-[9px] text-[#757675]">Notes for review: {meta.internal_notes.join("; ")}</p>}
          <p className="text-[9px] text-[#757675] font-mono flex items-center gap-1">
            {note?.ai_job_id ? `Job ${note.ai_job_id.substring(0, 12)}` : "No AI job reference"}
            {meta.prompt_version ? ` · prompt ${String(meta.prompt_version).startsWith("v") ? meta.prompt_version : `v${meta.prompt_version}`}` : ""}
            {note?.ai_job_id && <CopyButton value={note.ai_job_id} label="Copy job ID" toastMessage="Job ID copied" size="xs" />}
          </p>
          {note?.ai_transcript_deleted_at && <p className="text-[9px] text-[#757675]">Transcript deleted: {note.ai_transcript_deleted_at.split("T")[0]}</p>}
        </div>
      )}
    </div>
  );
};

// Email modal for sidebar note actions - sends a secure link, never clinical detail
function SidebarEmailModal({ note, patient, clinic, user, onClose }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);
  const to = patient?.email;

  const handleSend = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await api.post(`/clinical/notes/${note?.id}/email`, { message: message || null });
      if (data.email_status === "sent" || data.email_status === "mock") {
        setSent(data);
      } else {
        setError(data.message || "Email could not be sent. Please try again or download the PDF and send it manually.");
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Email could not be sent. Please try again or download the PDF and send it manually.");
    }
    setSending(false);
  };

  if (sent) return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 px-4" data-testid="note-email-sent">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
        <CheckCircle2 className="w-8 h-8 text-[#205B5A] mx-auto mb-2" />
        <p className="text-sm font-medium text-[#1A1B1A]">Treatment note email sent</p>
        <p className="text-[10px] text-[#757675] mb-3">{sent?.message || `Sent to ${to}`}</p>
        <button onClick={onClose} className="bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-xs font-medium" data-testid="note-email-done">Done</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 px-4" data-testid="sidebar-email-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-medium text-[#1A1B1A]">Email treatment note</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-[#757675]" /></button>
        </div>

        {!to ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5 mb-3" data-testid="note-email-no-address">
            <p className="text-[11px] text-amber-700">This patient does not have an email address saved.</p>
          </div>
        ) : (
          <div className="bg-[#FAF9F6] rounded-xl px-3 py-2.5 mb-3 space-y-1" data-testid="note-email-summary">
            <p className="text-[11px] text-[#4A4B4A]">To <span className="text-[#1A1B1A]">{to}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Record <span className="text-[#1A1B1A]">{note?.appointment_type || "Treatment note"}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Date <span className="text-[#1A1B1A]">{String(note?.date || note?.created_at || "").slice(0, 10)}</span></p>
            <p className="text-[11px] text-[#4A4B4A]">Practitioner <span className="text-[#1A1B1A]">{note?.practitioner_name || user?.full_name || user?.name || "—"}</span></p>
            <p className="text-[10px] text-[#757675] pt-1">
              The patient receives a secure link to view, print or save this record. No clinical detail is included in the email itself. Link expires in 30 days.
            </p>
          </div>
        )}

        <div>
          <label className="text-[10px] text-[#757675] mb-0.5 block">Short note to the patient (optional)</label>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={3} data-testid="note-email-message"
            placeholder="e.g. Here is the record from your visit today."
            className="w-full px-3 py-2 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none" />
        </div>

        {error && (
          <p className="text-[11px] text-red-600 mt-2" data-testid="note-email-error">{error}</p>
        )}

        <div className="flex justify-end gap-2 mt-3">
          <button onClick={onClose} className="px-3 py-1.5 text-xs text-[#757675]">Cancel</button>
          <button onClick={handleSend} disabled={sending || !to} data-testid="note-email-send"
            className="bg-[#205B5A] text-white px-4 py-1.5 rounded-full text-xs font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-1">
            <Send className="w-3 h-3" /> {sending ? "Sending..." : "Send secure link"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TreatmentNoteEditor({ patientId, patientData, existingNote: existingNoteProp, onClose, onSaved }) {
  // Full note record (list payloads omit transcript/field_values, so fetch the detail once)
  const [noteDetail, setNoteDetail] = useState(null);
  const existingNote = noteDetail || existingNoteProp;
  // Form state is keyed by section.id (not semantic_type) to prevent field duplication
  const [form, setForm] = useState({ appointment_type: "", status: "draft" });
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  const selectedTemplateRef = useRef(null);
  const [clinicTemplates, setClinicTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  useEffect(() => { selectedTemplateRef.current = selectedTemplate; }, [selectedTemplate]);
  // Track whether form has been initialized with the selected template
  const [formInitialized, setFormInitialized] = useState(false);
  const [bodyCharts, setBodyCharts] = useState(existingNote?.body_charts || []);
  const [showBodyChartEditor, setShowBodyChartEditor] = useState(null);
  const [showBodyChart, setShowBodyChart] = useState(false);
  const [lightboxChart, setLightboxChart] = useState(null);
  const [prevNotes, setPrevNotes] = useState([]);
  const [rightTab, setRightTab] = useState("previous");
  const [noteFilter, setNoteFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [expandedPrev, setExpandedPrev] = useState(null);
  const [pinnedNotes, setPinnedNotes] = useState(new Set());
  const [notesPage, setNotesPage] = useState(1);
  const NOTES_PER_PAGE = 5;
  const [sidebarEmailNote, setSidebarEmailNote] = useState(null);
  const { user, clinic } = useAuth();

  // Format date as DD/MM/YYYY
  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = iso.split("T")[0];
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  // Load a draft note into the editor
  const loadDraft = (note) => {
    // Re-initialize form with the draft note, mapping to current template's section IDs
    if (selectedTemplate?.sections) {
      const initializedForm = initializeFormState(note, selectedTemplate.sections);
      setForm(initializedForm);
    } else {
      setForm({ ...note, appointment_type: note.appointment_type || "" });
    }
    if (note.body_charts) setBodyCharts(note.body_charts);
    setExpandedPrev(null);
  };

  // Export a specific note as PDF using shared system
  const exportNotePdf = async (note) => {
    await generateNotePdf(note, { patient: p, clinic, getTemplateImageFn: getTemplateImage });
  };

  // Open email modal for a specific note
  const openNoteEmail = (note) => {
    setSidebarEmailNote(note);
  };
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiStage, setAiStage] = useState("");
  const [aiError, setAiError] = useState("");
  const [aiErrorDetail, setAiErrorDetail] = useState(null);
  const [aiBlockedCode, setAiBlockedCode] = useState("");
  const [aiUsageWarning, setAiUsageWarning] = useState(null);

  const [activeJobId, setActiveJobId] = useState(null);
  const [pendingAiNote, setPendingAiNote] = useState(null);
  const [retryingJob, setRetryingJob] = useState(false);
  const [transcript, setTranscript] = useState(existingNote?.transcript || "");
  const [rawTranscript, setRawTranscript] = useState(""); // Raw transcript for debugging
  const [diarizedSegments, setDiarizedSegments] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [aiDraftApplied, setAiDraftApplied] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(null);
  
  // AI draft state — fields are auto-filled, clinician reviews and saves
  const [aiStructuredNote, setAiStructuredNote] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState({}); // sectionId -> AI text for fields the clinician already filled
  const [preAiSnapshot, setPreAiSnapshot] = useState(null);
  const [aiFillSummary, setAiFillSummary] = useState(null);
  const [showAiDetails, setShowAiDetails] = useState(false);
  const [aiJobDebugInfo, setAiJobDebugInfo] = useState(null); // For admin/dev debug
  
  // Detect Safari browser for warning banner
  const isSafari = typeof navigator !== 'undefined' && 
    navigator.userAgent.includes('Safari') && 
    !navigator.userAgent.includes('Chrome');

  const p = patientData?.patient;
  const isReadOnly = existingNote?.status === "final";
  const showSavedTranscriptPanel = Boolean(
    existingNote?.id && !aiDraftApplied && !aiProcessing &&
    (noteDetail?.ai_transcript || noteDetail?.ai_transcript_deleted_at)
  );

  useEffect(() => {
    if (existingNoteProp?.id) {
      api.get(`/clinical/notes/detail/${existingNoteProp.id}`).then(r => setNoteDetail(r.data)).catch(() => {});
    }
  }, [existingNoteProp?.id]);

  // Restore the AI transcript / processing details saved with the note
  useEffect(() => {
    if (!noteDetail) return;
    setFormInitialized(false); // re-initialise once the full record (field_values) is available
    if (noteDetail.ai_transcript) setTranscript(noteDetail.ai_transcript);
    if (noteDetail.ai_diarized_segments?.length) setDiarizedSegments(noteDetail.ai_diarized_segments);
    if (noteDetail.ai_meta || noteDetail.ai_job_id) {
      setAiJobDebugInfo({
        jobId: noteDetail.ai_job_id,
        promptVersion: noteDetail.ai_meta?.prompt_version,
        modelUsed: noteDetail.ai_meta?.model_used,
        transcriptLength: (noteDetail.ai_transcript || "").length,
        segmentCount: noteDetail.ai_diarized_segments?.length || 0,
      });
      setAiFillSummary({
        filledCount: noteDetail.ai_meta?.fields_filled_count || 0,
        suggestionCount: 0,
        blankFields: noteDetail.ai_meta?.fields_left_blank || [],
        detailProfile: noteDetail.ai_meta?.detail_profile || "",
        internalNotes: noteDetail.ai_meta?.internal_notes || [],
      });
    }
  }, [noteDetail]);

  useEffect(() => {
    if (patientId) {
      api.get(`/clinical/notes/${patientId}`).then(r => setPrevNotes(r.data.notes.filter(n => n.id !== existingNote?.id))).catch(() => {});
    }
  }, [patientId, existingNote?.id]);

  // Load clinic templates and normalize sections
  useEffect(() => {
    api.get("/note-templates").then(r => {
      // Normalize all templates to ensure unique section IDs
      const templates = r.data.templates.map(t => ({
        ...t,
        sections: normalizeTemplateSections(t.sections)
      }));
      setClinicTemplates(templates);
      
      // Auto-select: match existing note's template, or appointment type default, or first
      let selected = null;
      // Saved notes always render from their own template snapshot, so later template edits
      // can never hide (or drop) content that was saved against the original fields.
      if (existingNote?.id && existingNote?.template_snapshot?.sections?.length) {
        const snap = existingNote.template_snapshot;
        selected = {
          id: snap.template_id || existingNote.template_id || snap.id,
          name: snap.template_name || snap.name || "Saved template",
          sections: normalizeTemplateSections([...snap.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))),
        };
      }
      if (!selected && existingNote?.template_id) {
        selected = templates.find(t => t.id === existingNote.template_id);
      }
      if (!selected && existingNote?.appointment_type) {
        selected = templates.find(t => t.name === existingNote.appointment_type);
      }
      if (!selected) {
        selected = templates[0] || null;
      }
      setSelectedTemplate(selected);
    }).catch(() => {});
  }, [existingNote]);

  // Initialize form state when template is selected (or changed)
  // This maps existing note values to the correct section IDs
  useEffect(() => {
    if (selectedTemplate && !formInitialized) {
      const initializedForm = initializeFormState(existingNote, selectedTemplate.sections);
      setForm(initializedForm);
      setFormInitialized(true);
    }
  }, [selectedTemplate, existingNote, formInitialized]);

  const selectTemplate = (templateId) => {
    const t = clinicTemplates.find(x => x.id === templateId);
    if (t) { 
      setSelectedTemplate(t); 
      // When switching templates, preserve existing values but re-key to new template's section IDs
      setForm(f => {
        const newForm = { 
          ...f, 
          appointment_type: t.name, 
          template_id: t.id 
        };
        // Clear old section values and map any matching semantic types
        const oldSemTypeValues = {};
        if (selectedTemplate?.sections) {
          for (const oldSec of selectedTemplate.sections) {
            const val = f[oldSec.id];
            if (val) {
              const semType = oldSec.semantic_type || "free_text";
              if (!oldSemTypeValues[semType]) oldSemTypeValues[semType] = val;
            }
          }
        }
        // Map to new template sections
        for (const newSec of t.sections) {
          const semType = newSec.semantic_type || "free_text";
          if (oldSemTypeValues[semType] && !newForm[newSec.id]) {
            newForm[newSec.id] = oldSemTypeValues[semType];
          }
        }
        return newForm;
      });
      // Silently re-map any AI draft onto the newly selected template
      if (aiStructuredNote) {
        setTimeout(() => applyAiDraft(aiStructuredNote, t.sections, { snapshot: false }), 0);
      }
    }
  };

  const copyPrevious = (note) => {
    if (!selectedTemplate?.sections) return;
    
    setForm(f => {
      const updated = { ...f };
      
      // Map previous note values to current template's section IDs
      for (const section of selectedTemplate.sections) {
        const sectionId = section.id;
        const semType = section.semantic_type;
        const legacyKey = SEMANTIC_TO_LEGACY_FIELD[semType] || semType;
        
        // Try to find a value from the previous note
        // Priority: section.id → semantic_type → legacy field key
        const value = note[sectionId] || note[semType] || note[legacyKey];
        if (value) {
          updated[sectionId] = value;
        }
      }
      
      return updated;
    });
  };

  // ── AI draft auto-fill ──────────────────────────────────────────────────────
  // Map the AI note onto the selected template's section IDs.
  // Priority: exact section id → matching field title → matching semantic type → legacy keys.
  const mapAiNoteToTemplate = (note, sections) => {
    if (!note || !sections?.length) return {};
    const aiFields = note.fields || {};
    const aiLabels = note.field_labels || {};
    const aiSemantics = note.field_semantic_types || {};
    const entries = Object.entries(aiFields).filter(([, v]) => typeof v === "string" && v.trim());
    const used = new Set();
    const mapped = {};

    for (const section of sections) {
      let value = "";

      if (aiFields[section.id]?.trim() && !used.has(section.id)) {
        value = aiFields[section.id];
        used.add(section.id);
      }

      if (!value) {
        const target = (section.label || "").trim().toLowerCase();
        const hit = entries.find(([k]) => !used.has(k) && (aiLabels[k] || "").trim().toLowerCase() === target);
        if (hit) { value = hit[1]; used.add(hit[0]); }
      }

      if (!value) {
        const semType = section.semantic_type || "free_text";
        if (semType !== "free_text") {
          const hit = entries.find(([k]) => !used.has(k) && aiSemantics[k] === semType);
          if (hit) { value = hit[1]; used.add(hit[0]); }
        }
      }

      if (!value) {
        // Legacy AI output shape (flat keys) — used by older jobs and the non-template fallback
        const semType = section.semantic_type || "free_text";
        const legacyKey = SEMANTIC_TO_LEGACY_FIELD[semType];
        value = note[semType] || (legacyKey ? note[legacyKey] : "") || "";
        if (!value) {
          if (semType === "current_primary_issue") value = note.presenting_complaint || "";
          else if (semType === "symptom_progression") value = note.history_of_presenting_complaint || "";
          else if (semType === "examination_findings") value = note.assessment || "";
          else if (semType === "treatment_given") value = note.treatment || "";
          else if (semType === "follow_up_plan") value = note.treatment_plan || "";
          else if (semType === "medical_conditions") value = note.medical_history || "";
        }
      }

      if (typeof value === "string" && value.trim()) mapped[section.id] = value.trim();
    }
    return mapped;
  };

  // Auto-fill note fields from an AI note. Empty fields are filled directly;
  // fields the clinician already typed in are preserved and offered as suggestions.
  const applyAiDraft = (note, sections, { snapshot = true } = {}) => {
    // Async callbacks (job polling, refresh resume) may run before the template loaded,
    // so always fall back to the latest template from the ref.
    sections = sections?.length ? sections : (selectedTemplateRef.current?.sections || []);
    if (!note || !sections.length) {
      setPendingAiNote(note || null);   // apply as soon as the template is available
      return;
    }
    const mapped = mapAiNoteToTemplate(note, sections);
    const current = formRef.current || {};
    if (snapshot) {
      const snap = {};
      for (const s of sections) snap[s.id] = current[s.id] ?? "";
      setPreAiSnapshot(snap);
    }

    const suggestions = {};
    const filled = [];
    const updates = {};
    for (const [sid, value] of Object.entries(mapped)) {
      const existing = (current[sid] ?? "").toString().trim();
      if (!existing) {
        updates[sid] = value;
        filled.push(sid);
      } else if (existing !== value) {
        suggestions[sid] = value;
      }
    }

    setForm(f => ({ ...f, ...updates }));
    setAiSuggestions(suggestions);
    setAiDraftApplied(true);
    setAiFillSummary({
      filledCount: filled.length,
      suggestionCount: Object.keys(suggestions).length,
      blankFields: note.fields_left_blank || [],
      detailProfile: note.detail_profile || "",
      internalNotes: note.internal_uncertainty_notes || note.uncertain_items || [],
    });
  };

  // Restore the note to its pre-AI state
  const discardAiDraft = () => {
    if (preAiSnapshot) setForm(f => ({ ...f, ...preAiSnapshot }));
    setAiSuggestions({});
    setAiDraftApplied(false);
    setAiFillSummary(null);
    setAiStructuredNote(null);
    setShowAiDetails(false);
  };

  const acceptSuggestion = (sectionId) => {
    const value = aiSuggestions[sectionId];
    if (!value) return;
    setForm(f => ({ ...f, [sectionId]: value }));
    setAiSuggestions(s => { const n = { ...s }; delete n[sectionId]; return n; });
  };

  const dismissSuggestion = (sectionId) => {
    setAiSuggestions(s => { const n = { ...s }; delete n[sectionId]; return n; });
  };

  const saveBodyChart = (chartData) => {
    if (showBodyChartEditor?.index !== undefined) {
      setBodyCharts(prev => prev.map((c, i) => i === showBodyChartEditor.index ? chartData : c));
    } else {
      setBodyCharts(prev => [...prev, chartData]);
    }
    setShowBodyChartEditor(null);
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // Browser Audio Format Detection - Safari/Chrome/Firefox compatible
  // ══════════════════════════════════════════════════════════════════════════════
  
  // CRITICAL: Map MIME types to file extensions
  // Safari records in audio/mp4 (AAC), Chrome/Edge in audio/webm (Opus)
  const MIME_TO_EXTENSION = {
    'audio/mp4': '.mp4',
    'audio/x-m4a': '.m4a',
    'audio/m4a': '.m4a',
    'audio/aac': '.aac',
    'audio/mpeg': '.mp3',
    'audio/mp3': '.mp3',
    'audio/webm': '.webm',
    'audio/webm;codecs=opus': '.webm',
    'audio/ogg': '.ogg',
    'audio/ogg;codecs=opus': '.ogg',
    'audio/wav': '.wav',
    'audio/wave': '.wav',
    'audio/x-wav': '.wav',
  };
  
  // Get file extension from MIME type (handles Safari's audio/mp4 correctly)
  const getExtensionFromMime = (mimeType) => {
    if (!mimeType) return '.webm'; // Safe fallback
    
    // Direct lookup first
    const lowerMime = mimeType.toLowerCase();
    if (MIME_TO_EXTENSION[lowerMime]) {
      return MIME_TO_EXTENSION[lowerMime];
    }
    
    // Partial match for codec variants
    if (lowerMime.includes('mp4') || lowerMime.includes('m4a')) return '.mp4';
    if (lowerMime.includes('aac')) return '.aac';
    if (lowerMime.includes('mpeg') || lowerMime.includes('mp3')) return '.mp3';
    if (lowerMime.includes('ogg')) return '.ogg';
    if (lowerMime.includes('wav')) return '.wav';
    if (lowerMime.includes('webm')) return '.webm';
    
    // Final fallback - check browser
    const ua = navigator.userAgent;
    const isSafari = ua.includes('Safari') && !ua.includes('Chrome');
    return isSafari ? '.mp4' : '.webm';
  };
  
  const detectBestAudioFormat = () => {
    // Format priority list - ordered by compatibility and quality
    // Safari prefers MP4/AAC, Chrome/Edge prefer WebM/Opus
    const formatPriority = [
      { mimeType: 'audio/webm;codecs=opus', ext: '.webm', name: 'WebM/Opus' },
      { mimeType: 'audio/webm', ext: '.webm', name: 'WebM' },
      { mimeType: 'audio/mp4', ext: '.mp4', name: 'MP4/AAC' },  // Safari's native format
      { mimeType: 'audio/aac', ext: '.aac', name: 'AAC' },
      { mimeType: 'audio/ogg;codecs=opus', ext: '.ogg', name: 'OGG/Opus' },
      { mimeType: 'audio/ogg', ext: '.ogg', name: 'OGG' },
      { mimeType: 'audio/wav', ext: '.wav', name: 'WAV' },
    ];

    // Detect browser for logging
    const ua = navigator.userAgent;
    let browserName = 'Unknown';
    if (ua.includes('Safari') && !ua.includes('Chrome')) browserName = 'Safari';
    else if (ua.includes('Chrome')) browserName = 'Chrome';
    else if (ua.includes('Firefox')) browserName = 'Firefox';
    else if (ua.includes('Edge')) browserName = 'Edge';

    // Find first supported format
    let selectedFormat = null;
    for (const format of formatPriority) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(format.mimeType)) {
        selectedFormat = format;
        break;
      }
    }

    // Fallback to browser default if nothing explicitly supported
    // Use browser-appropriate extension (Safari → .mp4, others → .webm)
    if (!selectedFormat) {
      const defaultExt = browserName === 'Safari' ? '.mp4' : '.webm';
      selectedFormat = { mimeType: '', ext: defaultExt, name: 'Browser Default' };
    }

    const formatInfo = {
      browser: browserName,
      userAgent: ua.substring(0, 100),
      selectedMimeType: selectedFormat.mimeType || 'browser default',
      selectedExt: selectedFormat.ext,
      formatName: selectedFormat.name,
      supportedFormats: formatPriority.filter(f => 
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(f.mimeType)
      ).map(f => f.name)
    };

    console.log('[AI Recording] Browser format detection:', formatInfo);
    return selectedFormat;
  };

  // ── Recording ──
  const startRecording = async () => {
    try {
      // Preflight: plan, AI switch, fair usage and note templates, checked before we record
      try {
        const { data: pre } = await api.get("/ai/preflight");
        if (!pre.allowed) {
          setAiError(pre.reason || "AI note processing is unavailable. Please ask an admin to check AI settings.");
          setAiBlockedCode(pre.code || "");
          return;
        }
        setAiBlockedCode("");
        setAiUsageWarning(pre.warn_at_80 ? pre.usage : null);
      } catch (preErr) {
        const detail = preErr.response?.data?.detail;
        setAiError(typeof detail === "object" ? detail.message
          : "AI note processing is unavailable. Please retry, or ask an admin to check AI settings.");
        return;
      }

      // Clear any previous AI draft state
      setAiStructuredNote(null);
      setAiSuggestions({});
      setAiFillSummary(null);
      setShowAiDetails(false);
      setAiDraftApplied(false);
      setPreAiSnapshot(null);
      setRawTranscript("");
      
      // Detect best audio format for this browser
      const audioFormat = detectBestAudioFormat();
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Create recorder with detected format (or use browser default if empty)
      const recorderOptions = audioFormat.mimeType ? { mimeType: audioFormat.mimeType } : {};
      let recorder;
      
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch (formatErr) {
        // If specified format fails, fall back to browser default
        console.warn('[AI Recording] Format not supported, using browser default:', formatErr.message);
        recorder = new MediaRecorder(stream);
      }
      
      // Store format info for upload - CRITICAL: Use actual recorded MIME type
      // Safari may return empty string for recorder.mimeType, so check all sources
      const actualMimeType = recorder.mimeType || audioFormat.mimeType || 'audio/webm';
      // CRITICAL FIX: Use centralized MIME-to-extension mapping
      // This ensures Safari's audio/mp4 → .mp4, not .webm
      const fileExt = getExtensionFromMime(actualMimeType);
      
      console.log('[AI Recording] Recorder initialized:', {
        requestedMimeType: audioFormat.mimeType,
        actualMimeType: recorder.mimeType,
        resolvedMimeType: actualMimeType,
        fileExtension: fileExt,
        browser: audioFormat.browser || 'Unknown'
      });

      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: actualMimeType });
        
        // Store format info in blob for upload
        blob._audioFormat = {
          mimeType: actualMimeType,
          ext: fileExt,
          browser: audioFormat.browser || 'Unknown'
        };
        
        await processAudio(blob);
      };
      recorder.start(1000); // collect in 1s chunks for reliability
      setMediaRecorder(recorder);
      setRecording(true);
      setAiError("");
      setAiErrorDetail(null);
      setRecordingDuration(0);
      const timer = setInterval(() => setRecordingDuration(d => d + 1), 1000);
      setRecordingTimer(timer);
    } catch (err) {
      console.error('[AI Recording] Failed to start recording:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setAiError("Microphone access denied. Please allow microphone access to record.");
      } else if (err.name === 'NotFoundError') {
        setAiError("No microphone found. Please connect a microphone and try again.");
      } else if (err.name === 'NotSupportedError') {
        setAiError("Audio recording is not supported in this browser. Please try Chrome, Safari, or Edge.");
      } else {
        setAiError("Could not start recording. Please check your microphone and try again.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
      mediaRecorder.stop();
      setRecording(false);
      if (recordingTimer) { clearInterval(recordingTimer); setRecordingTimer(null); }
    }
  };

  const fmtDur = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // Apply an AI note that arrived before the template finished loading
  useEffect(() => {
    if (pendingAiNote && selectedTemplate?.sections?.length) {
      const note = pendingAiNote;
      setPendingAiNote(null);
      applyAiDraft(note, selectedTemplate.sections);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAiNote, selectedTemplate?.id]);

  // ── Long-running AI job persistence (survives a page refresh) ──
  const jobStorageKey = `lf_ai_job_${patientId || "unknown"}`;

  const persistActiveJob = (jobId, estimatedMinutes = 0) => {
    setActiveJobId(jobId);
    try {
      localStorage.setItem(jobStorageKey, JSON.stringify({
        jobId, estimatedMinutes, noteId: existingNote?.id || null, savedAt: Date.now(),
      }));
    } catch { /* storage unavailable */ }
  };

  const clearActiveJob = () => {
    setActiveJobId(null);
    try { localStorage.removeItem(jobStorageKey); } catch { /* ignore */ }
  };

  // Resume polling an in-flight job after a refresh or reopen
  useEffect(() => {
    let cancelled = false;
    const resume = async () => {
      if (activeJobId || aiProcessing) return;  // already polling
      let stored;
      try { stored = JSON.parse(localStorage.getItem(jobStorageKey) || "null"); } catch { return; }
      if (!stored?.jobId) return;
      if (Date.now() - (stored.savedAt || 0) > 6 * 60 * 60 * 1000) { clearActiveJob(); return; }
      try {
        const { data: job } = await api.get(`/ai/job/${stored.jobId}`);
        if (cancelled) return;
        if (job.stage === "completed") {
          if (job.transcript) setTranscript(job.transcript);
          if (job.diarized_segments?.length) setDiarizedSegments(job.diarized_segments);
          if (job.structured_note) {
            setAiStructuredNote(job.structured_note);
            setAiJobDebugInfo({ jobId: job.id, promptVersion: job.prompt_version, modelUsed: job.model_used, transcriptLength: job.transcript?.length || 0, segmentCount: job.diarized_segments?.length || 0 });
            applyAiDraft(job.structured_note, selectedTemplateRef.current?.sections || selectedTemplate?.sections || []);
          }
          clearActiveJob();
        } else if (job.stage === "failed") {
          setActiveJobId(job.id);
          handleJobFailure(job);
        } else {
          setAiProcessing(true);
          setAiStage(job.stage_detail || "Processing in background...");
          pollAiJob(stored.jobId, stored.estimatedMinutes || 0);
        }
      } catch {
        clearActiveJob();
      }
    };
    resume();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, selectedTemplate?.id]);

  // Retry a failed job from the saved original recording
  const retryProcessing = async () => {
    const jobId = activeJobId || aiErrorDetail?.job_id;
    if (!jobId || retryingJob) return;
    setRetryingJob(true);
    setAiError("");
    setAiErrorDetail(null);
    setAiProcessing(true);
    setAiStage("Retrying processing...");
    try {
      await api.post(`/ai/job/${jobId}/retry`, {}, { timeout: 60000 });
      persistActiveJob(jobId, (recordingDuration || 0) / 60);
      setRetryingJob(false);
      await pollAiJob(jobId, (recordingDuration || 0) / 60);
    } catch (err) {
      setRetryingJob(false);
      setAiProcessing(false);
      setAiStage("");
      setAiError(err.response?.data?.detail || "We couldn't retry this recording. Please re-record.");
    }
  };

  // Poll a background AI job until it completes or fails. Reused by upload, retry and resume.
  const pollAiJob = async (jobId, estimatedMinutes = 0) => {
    const maxPollMinutes = Math.max(20, Math.ceil(20 + estimatedMinutes * 3));
    const maxAttempts = Math.ceil((maxPollMinutes * 60) / 2);
    setActiveJobId(jobId);
    try {
      // Poll for completion with adaptive timeout
      setAiStage(prev => prev || "Processing recording...");
      let attempts = 0;
      let consecutiveErrors = 0;
      const MAX_CONSECUTIVE_ERRORS = 5;
    
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000)); // poll every 2s
        attempts++;
      
        try {
          const { data: job } = await api.get(`/ai/job/${jobId}`);
          consecutiveErrors = 0; // Reset error counter on success
        
          // Debug log every 10 polls (20 seconds) or on stage change
          if (attempts % 10 === 1 || job.stage !== job._lastLoggedStage) {
            console.log('[AI Recording Debug] Poll status:', {
              jobId,
              attempt: attempts,
              stage: job.stage,
              stageDetail: job.stage_detail?.substring(0, 80),
              hasTranscript: !!job.transcript,
              transcriptLength: job.transcript?.length || 0,
              hasNote: !!job.structured_note,
              errorType: job.error_type || null
            });
            job._lastLoggedStage = job.stage;
          }

          // Update stage display with user-friendly messages
          if (job.stage === "queued") {
            setAiStage(job.stage_detail || "Recording saved. Processing in background...");
          } else if (job.stage === "preparing") {
            setAiStage(job.stage_detail || "Preparing audio...");
          } else if (job.stage === "transcribing") {
            const detail = job.stage_detail || "Transcribing consultation...";
            if (estimatedMinutes > 10) {
              setAiStage(`${detail} Longer consultations may take a few minutes.`);
            } else {
              setAiStage(detail);
            }
          } else if (job.stage === "transcribed") {
            setAiStage(job.stage_detail || "Generating treatment note...");
            if (job.transcript) setTranscript(job.transcript);
            if (job.diarized_segments?.length > 0) setDiarizedSegments(job.diarized_segments);
          } else if (job.stage === "compressing" || job.stage === "converting" || job.stage === "splitting" || job.stage === "preparing" || job.stage === "queued") {
            setAiStage(job.stage_detail || "Processing recording...");
          } else if (job.stage === "transcribed") {
            setAiStage(job.stage_detail || "Merging transcript...");
          } else if (job.stage === "summarising") {
            setAiStage(job.stage_detail || "Generating treatment note...");
          } else if (job.stage === "completed") {
            // Success - show transcript review before applying
            console.log('[AI Recording Debug] ✓ Job completed successfully:', {
              jobId,
              transcriptLength: job.transcript?.length || 0,
              segmentCount: job.diarized_segments?.length || 0,
              hasNote: !!job.structured_note,
              noteFields: job.structured_note ? Object.keys(job.structured_note).filter(k => job.structured_note[k]) : [],
              promptVersion: job.prompt_version,
              modelUsed: job.model_used
            });
          
            // Store raw transcript for debugging
            if (job.raw_transcript) setRawTranscript(job.raw_transcript);
            if (job.transcript) setTranscript(job.transcript);
            if (job.diarized_segments?.length > 0) setDiarizedSegments(job.diarized_segments);
          
            // Store debug info for the collapsed AI processing details panel
            setAiJobDebugInfo({
              jobId,
              promptVersion: job.prompt_version,
              modelUsed: job.model_used,
              templateSnapshot: job.template_snapshot,
              transcriptLength: job.transcript?.length || 0,
              segmentCount: job.diarized_segments?.length || 0
            });
          
            if (job.structured_note) {
              // Auto-fill the note fields immediately — no Apply step
              setAiStructuredNote(job.structured_note);
              applyAiDraft(job.structured_note, selectedTemplateRef.current?.sections || selectedTemplate?.sections || []);
            }
            clearActiveJob();
            setAiStage("");
            setAiProcessing(false);
            return;
          } else if (job.stage === "failed") {
            // Use centralized failure handler for consistent error handling and partial recovery
            console.error('[AI Recording Debug] ✗ Job failed:', {
              jobId,
              errorType: job.error_type,
              stageDetail: job.stage_detail,
              hasPartialTranscript: job.has_partial_transcript
            });
            handleJobFailure(job);
            return;
          }
        } catch (pollErr) {
          consecutiveErrors++;
          const status = pollErr.response?.status;
          const isNetworkError = !pollErr.response && (pollErr.code === 'ERR_NETWORK' || pollErr.message?.includes('Network Error'));
        
          console.warn('[AI Recording Debug] Poll error:', {
            jobId,
            attempt: attempts,
            consecutiveErrors,
            status,
            isNetworkError,
            errorCode: pollErr.code,
            errorMessage: pollErr.message?.substring(0, 100)
          });
        
          // Handle 404 specifically - job not found
          if (status === 404) {
            if (consecutiveErrors >= 3) {
              console.error('[AI Recording Debug] ✗ Job not found after 3 attempts:', jobId);
              // Job genuinely doesn't exist after multiple 404s
              setAiError("AI note processing failed. Please retry, or ask an admin to check AI settings.");
              setAiStage("");
              setAiProcessing(false);
              return;
            }
            // Could be a temporary issue, continue trying
            console.warn(`[AI Recording Debug] Poll 404 (attempt ${consecutiveErrors}/3)`);
          } else if (isNetworkError && consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('[AI Recording Debug] ✗ Network errors exceeded threshold:', consecutiveErrors);
            // Only show connection error if it's a genuine network issue
            setAiError("Connection issue. Please check your internet and try again.");
            setAiStage("");
            setAiProcessing(false);
            return;
          } else if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            console.error('[AI Recording Debug] ✗ Consecutive errors exceeded threshold:', consecutiveErrors);
            // Backend or processing error - NOT a connection issue
            // Try to get the job one more time to check for partial transcript
            try {
              const { data: finalJob } = await api.get(`/ai/job/${jobId}`);
              console.log('[AI Recording Debug] Final job check:', {
                jobId,
                stage: finalJob.stage,
                errorType: finalJob.error_type
              });
              if (finalJob.stage === "failed") {
                // Job failed with error from backend
                handleJobFailure(finalJob);
                return;
              }
            } catch (e) {
              console.error('[AI Recording Debug] Final job check failed:', e.message);
            }
          
            setAiError("We couldn't finish processing this recording. Please try again. If this keeps happening, contact support.");
            setAiStage("");
            setAiProcessing(false);
            return;
          }
        }
      }
      // Timed out polling - recording may still be processing
      console.warn('[AI Recording Debug] ⏱ Polling timeout after', maxAttempts, 'attempts');
      setAiError("This recording is still processing. Please wait a little longer or try again.");
      setAiStage("");
    } catch (err) {
      logger.error('AI job polling error:', err?.message);
      const isNetworkError = !err.response && (err.code === "ERR_NETWORK" || err.message?.includes("Network Error"));
      setAiError(isNetworkError
        ? "Connection issue. Please check your internet and try again."
        : "AI note processing failed. Please retry, or ask an admin to check AI settings.");
      setAiStage("");
    }
    setAiProcessing(false);
  };
  const processAudio = async (blob) => {
    setAiProcessing(true);
    setAiStage("Uploading recording...");
    setAiError("");
    setAiErrorDetail(null);
    
    // ═══════════════════════════════════════════════════════════════════════════
    // DEBUG: Log recording details (visible in browser console)
    // ═══════════════════════════════════════════════════════════════════════════
    const debugInfo = {
      timestamp: new Date().toISOString(),
      recordingDuration: recordingDuration,
      blobSize: blob.size,
      blobType: blob.type,
      estimatedMinutes: recordingDuration / 60,
    };
    console.log('[AI Recording Debug] Recording captured:', debugInfo);
    
    // Validate blob before upload
    if (!blob || blob.size === 0) {
      console.error('[AI Recording Debug] INVALID: Blob is empty or null');
      setAiError("Recording failed - no audio captured. Please try again.");
      setAiStage("");
      setAiProcessing(false);
      return;
    }
    
    if (blob.size < 1000) {
      console.warn('[AI Recording Debug] WARNING: Blob is very small:', blob.size, 'bytes');
    }
    
    // Calculate polling timeout based on actual recording duration (more accurate than file size)
    const actualDurationMinutes = recordingDuration / 60;
    const audioSizeBytes = blob.size;
    // Use actual duration if available, otherwise estimate from file size
    const estimatedMinutes = actualDurationMinutes > 0 
      ? actualDurationMinutes 
      : audioSizeBytes / (60 * 1024); // rough estimate
    
    
    try {
      // Get audio format info from blob (set during recording)
      // CRITICAL: Use centralized MIME-to-extension mapping for fallback
      const actualBlobType = blob.type || 'audio/webm';
      const audioFormat = blob._audioFormat || { 
        mimeType: actualBlobType, 
        ext: getExtensionFromMime(actualBlobType), 
        browser: 'Unknown' 
      };
      const fileName = `consultation${audioFormat.ext}`;
      
      // Enhanced debug logging for Safari diagnosis
      console.log('[AI Recording Debug] Upload file:', {
        fileName,
        mimeType: audioFormat.mimeType,
        blobType: blob.type,
        extension: audioFormat.ext,
        browser: audioFormat.browser,
        size: blob.size,
        durationSeconds: recordingDuration,
        userAgent: navigator.userAgent.substring(0, 80)
      });
      
      const formData = new FormData();
      formData.append("file", blob, fileName);
      formData.append("use_diarization", "true");
      if (form.appointment_id) formData.append("appointment_id", form.appointment_id);
      if (patientId) formData.append("patient_id", patientId);
      
      // Send template snapshot so the AI fills the clinician's actual fields
      if (selectedTemplate) {
        formData.append("template_id", selectedTemplate.id || "");
        formData.append("template_name", selectedTemplate.name || "");
        if (selectedTemplate.sections) {
          const templateFields = selectedTemplate.sections.map(s => ({
            id: s.id,
            label: s.label,
            semantic_type: s.semantic_type || "free_text",
            field_type: s.field_type || "long_text",
            ai_hint: s.ai_hint || "",
          }));
          formData.append("template_fields", JSON.stringify(templateFields));
        }
      }

      // Upload — long recordings go up in parts so one huge request can't fail the whole job
      setAiStage("Uploading recording...");
      
      const CHUNK_UPLOAD_THRESHOLD = 8 * 1024 * 1024;  // 8MB
      const UPLOAD_PART_SIZE = 4 * 1024 * 1024;        // 4MB per part
      let uploadRes;
      try {
        if (blob.size > CHUNK_UPLOAD_THRESHOLD) {
          const totalParts = Math.ceil(blob.size / UPLOAD_PART_SIZE);
          const { data: session } = await api.post("/ai/upload-session", {
            filename: fileName, total_parts: totalParts, total_bytes: blob.size,
          }, { timeout: 60000 });

          for (let i = 0; i < totalParts; i++) {
            setAiStage(`Uploading recording (part ${i + 1} of ${totalParts})...`);
            const part = blob.slice(i * UPLOAD_PART_SIZE, (i + 1) * UPLOAD_PART_SIZE);
            const partData = new FormData();
            partData.append("part_index", String(i));
            partData.append("file", part, `${fileName}.part${i}`);
            await api.post(`/ai/upload-session/${session.upload_id}/part`, partData, { timeout: 300000 });
          }

          setAiStage("Finalising upload...");
          const finishData = new FormData();
          finishData.append("upload_id", session.upload_id);
          for (const [k, v] of formData.entries()) {
            if (k !== "file") finishData.append(k, v);
          }
          const response = await api.post("/ai/process-consultation-chunked", finishData, { timeout: 120000 });
          uploadRes = response.data;
        } else {
          const response = await api.post("/ai/process-consultation", formData, { timeout: 300000 });
          uploadRes = response.data;
        }
      } catch (uploadErr) {
        // Upload failed - could be timeout, network, or server error
        const status = uploadErr.response?.status;
        const detail = uploadErr.response?.data?.detail || "";
        logger.error("Upload error:", { status, detail, code: uploadErr.code });
        
        if (status === 413 || detail.includes("too large")) {
          throw new Error("Recording file is too large. Please contact support if this persists.");
        } else if (status === 400) {
          throw new Error(detail || "Invalid recording format. Please try again.");
        } else if (status === 408 || uploadErr.code === "ECONNABORTED") {
          throw new Error("Upload timed out. Please check your internet connection and try again.");
        } else if (status >= 500) {
          throw new Error("Server error during upload. Please try again in a moment.");
        } else {
          throw new Error("AI note processing failed. Please retry, or ask an admin to check AI settings.");
        }
      }
      
      const jobId = uploadRes.job_id;
      if (!jobId) throw new Error("Upload succeeded but no job was created. Please try again.");
      persistActiveJob(jobId, estimatedMinutes);
      setAiStage("Recording saved. Processing in background...");
      
      console.log('[AI Recording Debug] Upload successful:', {
        jobId,
        initialStage: uploadRes.stage,
        timestamp: new Date().toISOString()
      });

      // Hand off to the shared job poller (survives refresh via the stored job id)
      await pollAiJob(jobId, estimatedMinutes);
      return;
    } catch (err) {
      // Final catch for any unhandled errors - show user-friendly message
      console.error('[AI Recording Debug] ✗ Unhandled error:', {
        name: err.name,
        message: err.message,
        code: err.code,
        isNetworkError: !err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error'))
      });
      const isNetworkError = !err.response && (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error'));
      const detail = err.response?.data?.detail;
      if (isNetworkError) {
        setAiError("Connection issue. Please check your internet and try again.");
      } else if (err.response?.status === 403 && detail) {
        // Plan / AI switch / fair usage: say exactly what is wrong
        setAiError(typeof detail === "object" ? detail.message : detail);
        setAiBlockedCode(typeof detail === "object" ? (detail.code || "") : "");
      } else {
        // For all non-network errors, use a friendly generic message (don't expose raw err.message)
        setAiError("AI note processing failed. Please retry, or ask an admin to check AI settings.");
      }
      setAiStage("");
    }
    setAiProcessing(false);
  };
  
  // Handle job failure with partial transcript recovery
  const handleJobFailure = (job) => {
    const failedAt = job.failed_stage || "processing";
    const hasPartialTranscript = job.has_partial_transcript || job.partial_transcript || job.transcript;
    const partialTranscript = job.partial_transcript || job.transcript || "";
    const errorType = job.error_type || "processing";
    const errorCode = job.error_code || null;
    const referenceId = job.reference_id || job.id?.substring(0, 12) || null;
    
    // Set transcript if available (for display)
    if (partialTranscript) {
      setTranscript(partialTranscript);
    }
    if (job.partial_segments?.length > 0 || job.diarized_segments?.length > 0) {
      setDiarizedSegments(job.partial_segments || job.diarized_segments);
    }
    
    // Build appropriate error message - now handled in UI
    let errorMsg = job.stage_detail?.replace(/^Failed at \w+: /, "") || "Processing failed. Please try again.";
    
    // Log full error details for debugging
    console.error('[AI Recording Debug] Job failed:', {
      jobId: job.id,
      errorCode,
      referenceId,
      errorType,
      failedAt,
      stageDetail: job.stage_detail,
      hasPartialTranscript
    });
    
    // Set error details for recovery UI - include error_code and reference_id
    setAiError(errorMsg);
    setAiErrorDetail({ 
      job_id: job.id,
      transcript_saved: !!job.transcript_id, 
      transcript_id: job.transcript_id, 
      stage: failedAt,
      stage_label: job.failed_stage_label || "",
      message: job.stage_detail || "",
      has_partial_transcript: hasPartialTranscript,
      error_type: errorType,
      error_code: errorCode,
      reference_id: referenceId,
      can_retry_processing: Boolean(job.original_audio_path),
      chunks_completed: job.chunks_completed,
      chunks_total: job.total_chunks || job.chunks_total
    });
    setActiveJobId(job.id);
    setAiStage("");
    setAiProcessing(false);
  };
  
  // Simple logger for debugging (console in dev)
  const logger = {
    error: (msg, data) => console.error(`[AI] ${msg}`, data || ""),
    info: (msg, data) => console.log(`[AI] ${msg}`, data || ""),
  };

  const retryFromTranscript = async (tId) => {
    const transcriptId = tId || aiErrorDetail?.transcript_id;
    if (!transcriptId && !transcript) return;
    setAiProcessing(true);
    setAiStage("Generating treatment note...");
    setAiError("");
    setAiErrorDetail(null);
    try {
      const templateSections = selectedTemplate?.sections?.map(s => ({
        id: s.id, label: s.label, semantic_type: s.semantic_type, field_type: s.field_type, ai_hint: s.ai_hint || ""
      }));
      const { data } = await api.post("/ai/summarise", {
        transcript,
        transcript_id: transcriptId || undefined,
        patient_id: patientId,
        appointment_id: form.appointment_id,
        template_sections: templateSections,
      }, { timeout: 240000 });
      if (data.structured_note) {
        setAiStructuredNote(data.structured_note);
        applyAiDraft(data.structured_note, selectedTemplateRef.current?.sections || selectedTemplate?.sections || []);
      }
      setAiStage("");
    } catch (err) { setAiError(err.response?.data?.detail || "Note generation failed."); setAiStage(""); }
    setAiProcessing(false);
  };

  const save = async (status) => {
    setSaving(true);
    
    // Build payload with section ID values
    // Also include semantic_type mappings for backward compatibility with note display
    // Every value from the selected template, keyed by field id (blank values included so
    // the template structure is preserved on reopen)
    // Start from what was already saved so values keyed by fields no longer in the
    // template are carried forward rather than silently dropped
    const fieldValues = { ...(existingNote?.field_values || {}) };
    const snapshotSections = (selectedTemplate?.sections || []).map((sec, order) => {
      fieldValues[sec.id] = form[sec.id] !== undefined ? form[sec.id] : "";
      return {
        id: sec.id,
        label: sec.label,
        semantic_type: sec.semantic_type || "free_text",
        field_type: sec.field_type || "long_text",
        ai_hint: sec.ai_hint || "",
        order,
      };
    });

    const payload = {
      ...form, 
      patient_id: patientId, 
      status,
      template_id: selectedTemplate?.id,
      field_values: fieldValues,
      template_snapshot: selectedTemplate ? {
        template_id: selectedTemplate.id,
        template_name: selectedTemplate.name,
        sections: snapshotSections,
        captured_at: new Date().toISOString(),
      } : null,
      body_charts: bodyCharts,
      body_regions: bodyCharts.flatMap(c => c.texts?.map(t => t.text) || []),
    };

    // Preserve any fields from the note's original snapshot that are not in the current template
    const savedSections = existingNote?.template_snapshot?.sections || [];
    const currentIds = new Set(snapshotSections.map(x => x.id));
    const carriedSections = savedSections.filter(x => !currentIds.has(x.id));
    if (carriedSections.length && payload.template_snapshot) {
      payload.template_snapshot.sections = [
        ...snapshotSections,
        ...carriedSections.map((x, i) => ({ ...x, order: snapshotSections.length + i })),
      ];
    }

    // Keep the AI trail with the note so the transcript survives a reopen
    if (transcript) {
      payload.ai_transcript = transcript;
      payload.ai_transcript_created_at = existingNote?.ai_transcript_created_at || new Date().toISOString();
      if (diarizedSegments?.length) payload.ai_diarized_segments = diarizedSegments;
    }
    if (aiJobDebugInfo?.jobId) payload.ai_job_id = aiJobDebugInfo.jobId;
    if (aiStructuredNote || aiFillSummary) {
      payload.ai_meta = {
        prompt_version: aiJobDebugInfo?.promptVersion || aiStructuredNote?.prompt_version || "",
        model_used: aiJobDebugInfo?.modelUsed || "",
        detail_profile: aiStructuredNote?.detail_profile || aiFillSummary?.detailProfile || "",
        passes: aiStructuredNote?.passes || null,
        fields_filled_count: aiFillSummary?.filledCount ?? null,
        fields_left_blank: aiStructuredNote?.fields_left_blank || aiFillSummary?.blankFields || [],
        internal_notes: aiStructuredNote?.internal_uncertainty_notes || aiFillSummary?.internalNotes || [],
        field_grounding: aiStructuredNote?.field_grounding || [],
      };
    }
    
    // Add legacy field mappings for backward compatibility with note list/display
    // This allows old code that expects 'presenting_complaint' etc. to still work
    if (selectedTemplate?.sections) {
      for (const section of selectedTemplate.sections) {
        const sectionId = section.id;
        const value = form[sectionId];
        if (value) {
          const semType = section.semantic_type;
          const legacyKey = SEMANTIC_TO_LEGACY_FIELD[semType] || semType;
          // Don't overwrite if already set (in case of multiple sections with same semantic_type)
          if (!payload[legacyKey]) {
            payload[legacyKey] = value;
          }
        }
      }
    }
    
    try {
      if (existingNote?.id) {
        await api.put(`/clinical/notes/${existingNote.id}`, payload);
      } else {
        await api.post("/clinical/notes", payload);
      }
      onSaved();
    } catch {}
    setSaving(false);
  };

  const filteredPrev = prevNotes.filter(n => {
    if (!noteFilter) return true;
    const q = noteFilter.toLowerCase();
    return (n.appointment_type || "").toLowerCase().includes(q) ||
      (n.practitioner_name || "").toLowerCase().includes(q) ||
      noteDisplayFields(n).some(f => f.label.toLowerCase().includes(q) || String(f.value).toLowerCase().includes(q));
  });

  // Sort: pinned first, then by date
  const sortedPrev = [...filteredPrev].sort((a, b) => {
    const aPin = pinnedNotes.has(a.id) ? 0 : 1;
    const bPin = pinnedNotes.has(b.id) ? 0 : 1;
    if (aPin !== bPin) return aPin - bPin;
    return (b.created_at || "").localeCompare(a.created_at || "");
  });

  return (
    <div className={`fixed inset-0 z-50 flex flex-col ${isReadOnly ? "bg-[#F0F2F1]" : "bg-[#FAF9F6]"}`} data-testid="treatment-note-editor">
      {/* Top bar */}
      <div className="bg-white border-b border-[#1A1B1A]/[0.06] px-5 py-2.5 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onClose} data-testid="note-editor-close" className="p-1.5 text-[#757675] hover:text-[#1A1B1A] hover:bg-[#FAF9F6] rounded-lg"><X className="w-4 h-4" /></button>
          <div>
            <h2 className="text-sm font-medium text-[#1A1B1A]" style={H}>{isReadOnly ? "View treatment note" : existingNote?.id ? "Edit treatment note" : "New treatment note"}</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[10px] text-[#757675]">{p?.first_name} {p?.last_name}</p>
              {existingNote?.practitioner_name && <span className="text-[10px] text-[#757675]">· {existingNote.practitioner_name}</span>}
              {existingNote?.created_at && <span className="text-[10px] text-[#757675]">· {existingNote.created_at.split("T")[0].split("-").reverse().join("/")}</span>}
              {existingNote?.updated_at && existingNote.status !== "final" && <span className="text-[10px] text-[#D4A843]">· Last updated {existingNote.updated_at.split("T")[0].split("-").reverse().join("/")}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {existingNote?.id && (
            <button onClick={() => openNoteEmail({ ...form, ...existingNote, appointment_type: form.appointment_type || existingNote.appointment_type })}
              data-testid="email-current-note-btn"
              className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-4 py-1.5 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1">
              <Mail className="w-3 h-3" /> Email to patient
            </button>
          )}
          {isReadOnly ? (
            <span data-testid="note-readonly-badge" className="text-[10px] font-semibold text-[#205B5A] bg-[#205B5A]/10 px-3 py-1 rounded-full flex items-center gap-1"><Lock className="w-3 h-3" /> Final — read-only</span>
          ) : (
            <>
              <span className={`text-[9px] font-semibold px-2.5 py-0.5 rounded-full ${form.status === "final" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"}`} data-testid="note-status-badge">{form.status === "final" ? "Final" : "Draft"}</span>
              <button onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn"
                className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-4 py-1.5 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1">
                <Save className="w-3 h-3" /> {saving ? "Saving..." : "Save draft"}
              </button>
              <button onClick={() => save("final")} disabled={saving} data-testid="save-final-btn"
                className="text-[11px] font-medium text-white bg-[#205B5A] px-4 py-1.5 rounded-full hover:bg-[#174544] flex items-center gap-1">
                <Lock className="w-3 h-3" /> {saving ? "..." : "Save as final"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* CENTER: Note editor */}
        <div className={`flex-1 overflow-y-auto ${isReadOnly ? "p-8" : "p-6"}`}>
          <div className={`max-w-2xl mx-auto ${isReadOnly ? "bg-white rounded-2xl shadow-sm border border-[#1A1B1A]/[0.05] p-6 md:p-8" : ""}`}>
            {/* Template + Appointment + Copy */}
            <div className="flex items-center gap-3 mb-5 flex-wrap">
              <div>
                <label className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider block mb-1">Template</label>
                <select value={selectedTemplate?.id || ""} onChange={e => selectTemplate(e.target.value)} disabled={isReadOnly} data-testid="template-select"
                  className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs font-medium text-[#1A1B1A] focus:outline-none focus:border-[#205B5A] bg-white min-w-[180px] disabled:opacity-60">
                  {selectedTemplate && !clinicTemplates.some(t => t.id === selectedTemplate.id) && (
                    <option value={selectedTemplate.id}>{selectedTemplate.name} (as saved)</option>
                  )}
                  {clinicTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[9px] font-semibold text-[#757675] uppercase tracking-wider block mb-1">Appointment</label>
                <select value={form.appointment_id || ""} onChange={e => setForm({...form, appointment_id: e.target.value})} disabled={isReadOnly}
                  className="px-3 py-2 rounded-lg border border-[#1A1B1A]/10 text-xs text-[#4A4B4A] focus:outline-none focus:border-[#205B5A] bg-white min-w-[160px] disabled:opacity-60">
                  <option value="">No linked appointment</option>
                  {patientData?.appointments?.slice(0, 15).map(a => <option key={a.id} value={a.id}>{a.date} {a.start_time} — {a.appointment_type_name}</option>)}
                </select>
              </div>
              {!isReadOnly && prevNotes.length > 0 && (
                <button onClick={() => copyPrevious(prevNotes[0])} data-testid="copy-previous-btn"
                  className="mt-4 text-[11px] font-medium text-[#205B5A] border border-[#205B5A]/20 px-3 py-2 rounded-lg hover:bg-[#205B5A]/5 flex items-center gap-1">
                  <Copy className="w-3 h-3" /> Copy previous note
                </button>
              )}
            </div>

            {/* Supporting AI transcript kept with a saved note */}
            {showSavedTranscriptPanel && (
              <SavedTranscriptPanel
                note={noteDetail || existingNote}
                transcript={noteDetail?.ai_transcript || ""}
                diarizedSegments={noteDetail?.ai_diarized_segments || []}
              />
            )}

            {/* AI Recording & Documentation */}
            {!isReadOnly && (
              <div className="mb-5 bg-[#205B5A]/[0.03] border border-[#205B5A]/10 rounded-xl p-4" data-testid="ai-transcription-area">
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${recording ? "bg-red-100" : "bg-[#205B5A]/10"}`}>
                    <Mic className={`w-4 h-4 ${recording ? "text-red-500 animate-pulse" : "text-[#205B5A]"}`} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-[#1A1B1A]">AI-assisted documentation</p>
                    <p className="text-[10px] text-[#757675]">
                      {recording ? `Recording... ${fmtDur(recordingDuration)}` :
                       aiProcessing ? aiStage || "Processing..." :
                       aiDraftApplied ? "AI draft added to note" :
                       "Record the consultation and auto-fill note fields"}
                    </p>
                  </div>
                  {!recording && !aiProcessing && (
                    <button onClick={startRecording} data-testid="start-recording-btn"
                      className="text-[11px] font-medium text-white bg-[#205B5A] px-3.5 py-1.5 rounded-full hover:bg-[#174544] flex items-center gap-1.5">
                      <Mic className="w-3 h-3" /> Start recording
                    </button>
                  )}
                  {recording && (
                    <button onClick={stopRecording} data-testid="stop-recording-btn"
                      className="text-[11px] font-medium text-white bg-red-500 px-3.5 py-1.5 rounded-full hover:bg-red-600 flex items-center gap-1.5">
                      <span className="w-2 h-2 bg-white rounded-sm" /> Stop recording
                    </button>
                  )}
                  {aiProcessing && (
                    <div className="flex items-center gap-2">
                      <span className="w-3.5 h-3.5 border-2 border-[#205B5A] border-t-transparent rounded-full animate-spin" />
                      <span className="text-[10px] text-[#205B5A] font-medium">Processing</span>
                    </div>
                  )}
                </div>

                {/* Safari beta warning banner */}
                {isSafari && !recording && !aiProcessing && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2" data-testid="safari-warning-banner">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-[10px] text-amber-700">
                        <span className="font-medium">Safari recording support is currently being tested.</span> For best results during beta testing, please use Chrome for AI recording.
                      </p>
                    </div>
                  </div>
                )}

                {/* Processing stages indicator */}
                {aiProcessing && aiStage && (
                  <div className="bg-[#205B5A]/[0.06] rounded-lg px-3 py-2 mb-2">
                    <p className="text-[10px] text-[#205B5A] font-medium flex items-center gap-1.5" data-testid="ai-processing-stage">
                      <span className="w-2 h-2 bg-[#205B5A] rounded-full animate-pulse" />
                      {aiStage}
                    </p>
                    {recordingDuration > 120 && <p className="text-[9px] text-[#205B5A]/70 mt-0.5">Longer consultations may take a little more time to process.</p>}
                  </div>
                )}

                {/* Error with stage-aware retry and partial transcript recovery */}
                {aiError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 mb-2" data-testid="ai-error-display">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-[10px] text-red-700 font-medium" data-testid="ai-error-message">
                          {aiError}
                        </p>
                        {aiBlockedCode && (
                          <p className="text-[10px] text-red-600 mt-0.5" data-testid="ai-blocked-hint">
                            {aiBlockedCode === "NO_NOTE_TEMPLATE"
                              ? "An owner can add one in Settings → Note templates."
                              : "An owner can review this in Settings → Billing & Plan."}
                          </p>
                        )}
                        {aiErrorDetail?.stage_label && (
                          <p className="text-[10px] text-red-600 mt-0.5" data-testid="ai-failed-stage">
                            Failed stage: {aiErrorDetail.stage_label}
                            {aiErrorDetail.chunks_total ? ` (${aiErrorDetail.chunks_completed ?? 0}/${aiErrorDetail.chunks_total} parts done)` : ""}
                          </p>
                        )}
                        {aiErrorDetail?.message && !aiErrorDetail?.stage_label && (
                          <p className="text-[10px] text-red-600 mt-0.5" data-testid="ai-failure-message">{aiErrorDetail.message}</p>
                        )}
                        {aiErrorDetail && aiErrorDetail.can_retry_processing === false && (
                          <p className="text-[10px] text-red-600 mt-0.5" data-testid="ai-no-original-audio">
                            Original recording is unavailable. Please re-record.
                          </p>
                        )}
                        
                        {/* Error code and reference ID for support - with copy buttons */}
                        {(aiErrorDetail?.error_code || aiErrorDetail?.reference_id) && (
                          <div className="mt-1.5 pt-1.5 border-t border-red-200/50 space-y-0.5">
                            {aiErrorDetail?.error_code && (
                              <p className="text-[9px] text-red-600 font-mono flex items-center gap-1">
                                Error code: {aiErrorDetail.error_code}
                                <CopyButton 
                                  value={aiErrorDetail.error_code} 
                                  label="Copy error code" 
                                  toastMessage="Error code copied"
                                  size="xs"
                                />
                              </p>
                            )}
                            {aiErrorDetail?.reference_id && (
                              <p className="text-[9px] text-red-600 font-mono flex items-center gap-1">
                                Reference ID: {aiErrorDetail.reference_id}
                                <CopyButton 
                                  value={aiErrorDetail.reference_id} 
                                  label="Copy reference ID" 
                                  toastMessage="Reference ID copied"
                                  size="xs"
                                />
                              </p>
                            )}
                          </div>
                        )}
                        
                        {aiErrorDetail?.transcript_saved && (
                          <p className="text-[9px] text-red-600 mt-1">Your transcript was saved successfully. You can retry note generation without re-recording.</p>
                        )}
                        {aiErrorDetail?.has_partial_transcript && aiErrorDetail?.stage === "transcription" && (
                          <p className="text-[9px] text-amber-600 mt-1">
                            {aiErrorDetail.chunks_total > 1 
                              ? `Partial transcript available (${aiErrorDetail.chunks_completed || 1} of ${aiErrorDetail.chunks_total} parts completed).`
                              : "Part of the consultation was transcribed."}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {aiErrorDetail?.can_retry_processing && (
                        <button onClick={retryProcessing} disabled={retryingJob} data-testid="retry-processing-btn"
                          className="text-[10px] font-medium text-white bg-[#205B5A] px-3 py-1 rounded-full hover:bg-[#174544] disabled:opacity-60 transition-colors">
                          {retryingJob ? "Retrying..." : "Retry processing"}
                        </button>
                      )}
                      {(aiErrorDetail?.reference_id || activeJobId) && (
                        <CopyButton
                          value={aiErrorDetail?.reference_id || activeJobId}
                          label="Copy reference"
                          toastMessage="Reference copied"
                          variant="button"
                          size="xs"
                        >
                          Copy reference
                        </CopyButton>
                      )}
                      {(aiErrorDetail?.transcript_saved || transcript) && (
                        <button onClick={() => retryFromTranscript(aiErrorDetail?.transcript_id)} data-testid="retry-summarise-btn"
                          className="text-[10px] font-medium text-[#205B5A] bg-[#205B5A]/10 px-3 py-1 rounded-full hover:bg-[#205B5A]/15 transition-colors">
                          Retry note generation
                        </button>
                      )}
                      {transcript && (
                        <CopyButton 
                          value={transcript}
                          label="Copy transcript"
                          toastMessage="Transcript copied"
                          variant="button"
                          size="xs"
                        >
                          Copy transcript
                        </CopyButton>
                      )}
                      <button onClick={startRecording} className="text-[10px] font-medium text-[#757675] hover:text-[#1A1B1A] transition-colors">Re-record</button>
                    </div>
                  </div>
                )}

                {/* Partial transcript recovery panel */}
                {aiError && (aiErrorDetail?.has_partial_transcript || transcript) && (
                  <div className="bg-amber-50/50 rounded-lg px-3 py-2 mb-2 border border-amber-200/50" data-testid="partial-transcript-panel">
                    <button 
                      onClick={() => setShowTranscript(!showTranscript)} 
                      className="w-full flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-amber-600" />
                        <span className="text-[10px] font-medium text-amber-700">
                          {aiErrorDetail?.stage === "transcription" ? "Partial transcript available" : "Transcript available"}
                        </span>
                        {diarizedSegments.length > 0 && (
                          <span className="text-[8px] bg-amber-200/50 text-amber-700 px-1.5 py-0.5 rounded-full">Speaker separated</span>
                        )}
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 text-amber-600 transition-transform ${showTranscript ? "rotate-180" : ""}`} />
                    </button>
                    {showTranscript && (
                      <div className="mt-2 bg-white rounded-lg border border-amber-200/30 p-2 max-h-40 overflow-y-auto">
                        {diarizedSegments.length > 0 ? (
                          <div className="space-y-1.5">
                            {diarizedSegments.map((seg, i) => (
                              <div key={i} className="flex gap-2">
                                <span className={`text-[8px] font-semibold px-1 py-0.5 rounded flex-shrink-0 mt-0.5 ${
                                  seg.speaker === "A" || seg.speaker === "Patient" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"
                                }`}>{seg.speaker === "A" ? "Patient" : seg.speaker === "B" ? "Practitioner" : seg.speaker}</span>
                                <p className="text-[9px] text-[#4A4B4A] leading-relaxed">{seg.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[9px] text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">{transcript}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* AI draft success state — fields are already populated */}
                {aiDraftApplied && !aiError && (
                  <div className="bg-[#205B5A]/[0.05] border border-[#205B5A]/20 rounded-lg px-3 py-3 mb-2" data-testid="ai-draft-applied-panel">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#205B5A] flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-[11px] font-medium text-[#205B5A]">AI draft added to note</p>
                        <p className="text-[10px] text-[#4A4B4A] mt-0.5">Review and edit the fields below before saving.</p>
                        {aiFillSummary?.suggestionCount > 0 && (
                          <p className="text-[10px] text-[#496275] mt-1" data-testid="ai-suggestion-notice">
                            {aiFillSummary.suggestionCount} field{aiFillSummary.suggestionCount > 1 ? "s" : ""} you had already written in {aiFillSummary.suggestionCount > 1 ? "were" : "was"} left untouched — an AI suggestion is available on those fields.
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mt-2.5">
                      <button
                        onClick={() => setShowTranscript(!showTranscript)}
                        data-testid="view-transcript-btn"
                        className="text-[10px] font-medium text-[#205B5A] bg-white border border-[#205B5A]/20 px-3 py-1 rounded-full hover:bg-[#205B5A]/5 flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" /> {showTranscript ? "Hide transcript" : "View transcript"}
                      </button>
                      <CopyButton
                        value={transcript}
                        label="Copy transcript"
                        toastMessage="Transcript copied"
                        variant="button"
                        size="xs"
                      >
                        Copy transcript
                      </CopyButton>
                      <button onClick={startRecording} data-testid="re-record-btn"
                        className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A]">
                        Re-record
                      </button>
                      <button onClick={discardAiDraft} data-testid="discard-ai-draft-btn"
                        className="text-[10px] font-medium text-[#757675] hover:text-red-500">
                        Discard AI draft
                      </button>
                    </div>

                    <p className="text-[9px] text-[#757675] mt-2">AI draft added. Please review before saving as final.</p>

                    {/* Optional, collapsed processing details */}
                    <button
                      onClick={() => setShowAiDetails(!showAiDetails)}
                      data-testid="ai-details-toggle"
                      className="mt-2 text-[9px] text-[#757675] hover:text-[#4A4B4A] flex items-center gap-1"
                    >
                      <ChevronDown className={`w-3 h-3 transition-transform ${showAiDetails ? "rotate-180" : ""}`} />
                      AI processing details
                    </button>
                    {showAiDetails && (
                      <div className="mt-1.5 bg-white/70 rounded-md border border-[#1A1B1A]/[0.06] p-2 space-y-1" data-testid="ai-details-panel">
                        <p className="text-[9px] text-[#4A4B4A]">Fields populated: {aiFillSummary?.filledCount ?? 0}</p>
                        {aiFillSummary?.detailProfile && (
                          <p className="text-[9px] text-[#4A4B4A]">Consultation length profile: {aiFillSummary.detailProfile}</p>
                        )}
                        {aiFillSummary?.blankFields?.length > 0 && (
                          <p className="text-[9px] text-[#4A4B4A]">Left blank (not supported by transcript): {aiFillSummary.blankFields.join(", ")}</p>
                        )}
                        {aiFillSummary?.internalNotes?.length > 0 && (
                          <p className="text-[9px] text-[#757675]">Notes for review: {aiFillSummary.internalNotes.join("; ")}</p>
                        )}
                        {aiJobDebugInfo && (
                          <p className="text-[9px] text-[#757675] font-mono flex items-center gap-1">
                            Job {aiJobDebugInfo.jobId?.substring(0, 12)} · prompt {String(aiJobDebugInfo.promptVersion || "").startsWith("v") ? aiJobDebugInfo.promptVersion : `v${aiJobDebugInfo.promptVersion}`} · {aiJobDebugInfo.transcriptLength} chars
                            {aiJobDebugInfo.jobId && (
                              <CopyButton value={aiJobDebugInfo.jobId} label="Copy job ID" toastMessage="Job ID copied" size="xs" />
                            )}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {transcript && !showSavedTranscriptPanel && (
                  <div>
                    <button onClick={() => setShowTranscript(!showTranscript)} className="text-[10px] font-medium text-[#4A4B4A] hover:text-[#205B5A] flex items-center gap-1">
                      <ChevronDown className={`w-3 h-3 transition-transform ${showTranscript ? "rotate-180" : ""}`} />
                      {showTranscript ? "Hide transcript" : "Show transcript"}
                      {diarizedSegments.length > 0 && <span className="text-[9px] bg-[#205B5A]/10 text-[#205B5A] px-1.5 py-0.5 rounded-full ml-1">Speaker separated</span>}
                    </button>
                    {showTranscript && (
                      <div className="mt-2 bg-white rounded-lg border border-[#1A1B1A]/[0.06] p-3 max-h-48 overflow-y-auto" data-testid="transcript-viewer">
                        {diarizedSegments.length > 0 ? (
                          <div className="space-y-2">
                            {diarizedSegments.map((seg, i) => (
                              <div key={i} className="flex gap-2">
                                <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5 ${
                                  seg.speaker === "A" || seg.speaker === "Patient" ? "bg-[#205B5A]/10 text-[#205B5A]" : "bg-[#496275]/10 text-[#496275]"
                                }`}>{seg.speaker === "A" ? "Patient" : seg.speaker === "B" ? "Practitioner" : seg.speaker}</span>
                                <p className="text-[10px] text-[#4A4B4A] leading-relaxed">{seg.text}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[10px] text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">{transcript}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Note fields from template sections */}
            <div className="space-y-4">
              {selectedTemplate?.sections?.map((section, sectionIndex) => {
                // CRITICAL: section.id is guaranteed unique after normalization
                // This is the ONLY key used for form state - never semantic_type
                const sectionId = section.id;
                const value = form[sectionId] || "";
                const baseRows = ["current_primary_issue", "examination_findings", "treatment_given"].includes(section.semantic_type) ? 4 : 3;
                // Grow with content so longer AI-filled fields stay readable and editable
                const autoRows = Math.min(28, Math.max(baseRows, value.split("\n").length + Math.ceil(value.length / 90)));
                const suggestion = aiSuggestions[sectionId];
                
                return (
                  <div key={sectionId} data-testid={`note-field-${sectionIndex}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className={`text-[10px] font-bold uppercase tracking-wider ${isReadOnly ? "text-[#2A3A35]" : "text-[#757675]"}`}>{section.label}</label>
                      {suggestion && !isReadOnly && (
                        <span className="text-[9px] text-[#496275] flex items-center gap-1.5" data-testid={`ai-suggestion-${sectionIndex}`}>
                          AI suggestion available
                          <button onClick={() => acceptSuggestion(sectionId)} data-testid={`ai-suggestion-use-${sectionIndex}`}
                            className="font-medium text-[#205B5A] hover:underline">Use</button>
                          <button onClick={() => dismissSuggestion(sectionId)} data-testid={`ai-suggestion-dismiss-${sectionIndex}`}
                            className="text-[#757675] hover:underline">Dismiss</button>
                        </span>
                      )}
                    </div>
                    {suggestion && !isReadOnly && (
                      <div className="mb-1.5 bg-[#496275]/[0.05] border border-[#496275]/15 rounded-lg px-2.5 py-1.5">
                        <p className="text-[10px] text-[#4A4B4A] leading-relaxed whitespace-pre-wrap">{suggestion}</p>
                      </div>
                    )}
                    {section.field_type === "checkbox" ? (
                      <label className="flex items-center gap-2"><input type="checkbox" checked={form[sectionId] || false} onChange={e => setForm({...form, [sectionId]: e.target.checked})} readOnly={isReadOnly} className="rounded border-[#1A1B1A]/20 text-[#205B5A]" /><span className="text-sm text-[#4A4B4A]">{section.label}</span></label>
                    ) : section.field_type === "short_text" ? (
                      <input value={value} onChange={e => setForm({...form, [sectionId]: e.target.value})} readOnly={isReadOnly}
                        data-testid={`note-input-${sectionIndex}`}
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm focus:outline-none transition-colors ${isReadOnly ? "bg-transparent border-transparent text-[#1A1B1A]" : "bg-white border-[#1A1B1A]/10 focus:border-[#205B5A]"}`}
                        placeholder={isReadOnly ? "" : `Enter ${section.label.toLowerCase()}...`} />
                    ) : (
                      <textarea value={value} onChange={e => setForm({...form, [sectionId]: e.target.value})} readOnly={isReadOnly}
                        data-testid={`note-textarea-${sectionIndex}`}
                        rows={autoRows}
                        className={`w-full px-3.5 py-2.5 rounded-xl border text-sm leading-relaxed focus:outline-none resize-y transition-colors ${isReadOnly ? "bg-transparent border-transparent text-[#1A1B1A]" : "bg-white border-[#1A1B1A]/10 focus:border-[#205B5A]"}`}
                        placeholder={isReadOnly ? "" : `Enter ${section.label.toLowerCase()}...`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Body Charts */}
            <div className="mt-6 mb-4">
              <button onClick={() => setShowBodyChart(!showBodyChart)} data-testid="toggle-body-chart"
                className="flex items-center gap-2 text-xs font-medium text-[#4A4B4A] hover:text-[#205B5A] transition-colors">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showBodyChart ? "rotate-180" : ""}`} />
                Body charts {bodyCharts.length > 0 && <span className="text-[9px] bg-[#205B5A]/10 text-[#205B5A] px-1.5 py-0.5 rounded-full">{bodyCharts.length} chart{bodyCharts.length > 1 ? "s" : ""}</span>}
              </button>
              {showBodyChart && (
                <div className="mt-3 bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-5" data-testid="body-chart-section">
                  {/* Existing charts - visual previews */}
                  {bodyCharts.length > 0 && (
                    <div className="space-y-4 mb-4">
                      {bodyCharts.map((chart, i) => (
                        <div key={i} data-testid={`saved-chart-${i}`}>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-[10px] font-semibold text-[#757675] uppercase tracking-wider capitalize">{chart.templateId?.replace(/-/g, " ")}</p>
                            <div className="flex gap-1.5">
                              {!isReadOnly && (
                                <button onClick={() => setShowBodyChartEditor({ chart, index: i })} data-testid={`edit-chart-${i}`}
                                  className="text-[9px] font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-0.5">
                                  <Pencil className="w-2.5 h-2.5" /> Edit
                                </button>
                              )}
                            </div>
                          </div>
                          <BodyChartPreview chart={chart} size="md" onClick={() => setLightboxChart(chart)} />
                          {chart.texts?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {chart.texts.map((t, ti) => <span key={ti} className="text-[9px] bg-[#FAF9F6] px-2 py-0.5 rounded-full text-[#4A4B4A]">{t.text}</span>)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add new chart */}
                  {!isReadOnly && (
                    <button onClick={() => setShowBodyChartEditor({})} data-testid="add-body-chart-btn"
                      className="w-full text-center py-3 border-2 border-dashed border-[#1A1B1A]/10 rounded-xl text-xs font-medium text-[#757675] hover:border-[#205B5A]/30 hover:text-[#205B5A] transition-colors flex items-center justify-center gap-1.5">
                      <Plus className="w-3.5 h-3.5" /> Add body chart
                    </button>
                  )}
                  {bodyCharts.length === 0 && isReadOnly && <p className="text-xs text-[#757675] text-center py-2">No body charts attached</p>}
                </div>
              )}
            </div>

            {/* Bottom actions */}
            {!isReadOnly && (
              <div className="flex items-center justify-between pt-4 pb-8 border-t border-[#1A1B1A]/[0.06] mt-6">
                <button className="text-[11px] text-[#757675] hover:text-red-500 flex items-center gap-1"><Archive className="w-3 h-3" /> Archive note</button>
                <div className="flex items-center gap-2">
                  <button onClick={() => save("draft")} disabled={saving} className="text-[11px] font-medium text-[#4A4B4A] border border-[#1A1B1A]/15 px-5 py-2 rounded-full hover:bg-[#FAF9F6] flex items-center gap-1.5">
                    <Save className="w-3 h-3" /> Save as draft
                  </button>
                  <button onClick={() => save("final")} disabled={saving} data-testid="bottom-save-final"
                    className="text-[11px] font-medium text-white bg-[#205B5A] px-5 py-2 rounded-full hover:bg-[#174544] flex items-center gap-1.5">
                    <Lock className="w-3 h-3" /> Save as final
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Context panel */}
        <div className="w-[300px] border-l border-[#1A1B1A]/[0.06] bg-white overflow-hidden flex flex-col flex-shrink-0 hidden lg:flex" data-testid="note-context-panel">
          <div className="flex border-b border-[#1A1B1A]/[0.06] flex-shrink-0">
            {[{ k: "previous", l: "Previous notes" }, { k: "patient", l: "Patient" }, { k: "appointments", l: "Appts" }].map(t => (
              <button key={t.k} onClick={() => setRightTab(t.k)}
                className={`flex-1 py-2.5 text-[10px] font-medium text-center border-b-2 -mb-px transition-colors ${rightTab === t.k ? "text-[#205B5A] border-[#205B5A]" : "text-[#757675] border-transparent"}`}>{t.l}</button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {rightTab === "previous" && (
              <div>
                <div className="relative mb-3">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-[#757675]" />
                  <input value={noteFilter} onChange={e => setNoteFilter(e.target.value)} data-testid="prev-notes-filter"
                    className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-[#1A1B1A]/8 text-[10px] focus:outline-none focus:border-[#205B5A]" placeholder="Filter notes..." />
                </div>
                {filteredPrev.length === 0 ? <p className="text-xs text-[#757675] py-4 text-center">No previous notes</p> : (
                  <div>
                    {/* Paginated note list */}
                    <div className="space-y-2">
                      {sortedPrev.slice((notesPage - 1) * NOTES_PER_PAGE, notesPage * NOTES_PER_PAGE).map(n => {
                        const isPinned = pinnedNotes.has(n.id);
                        const isExpanded = expandedPrev === n.id;
                        const isDraft = n.status !== "final";
                        return (
                        <div key={n.id} className={`rounded-xl overflow-hidden border transition-all ${isPinned ? "bg-[#205B5A]/[0.04] border-[#205B5A]/20" : isDraft ? "bg-[#FBF9F6] border-[#D4A843]/20" : "bg-white border-[#1A1B1A]/[0.06]"}`}>
                          <div className="p-2.5 cursor-pointer" onClick={() => setExpandedPrev(isExpanded ? null : n.id)}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <button onClick={e => { e.stopPropagation(); setPinnedNotes(prev => { const s = new Set(prev); s.has(n.id) ? s.delete(n.id) : s.add(n.id); return s; }); }}
                                className={`p-0.5 rounded transition-colors flex-shrink-0 ${isPinned ? "text-[#205B5A]" : "text-[#B0BEC5] hover:text-[#757675]"}`}>
                                <Pin className="w-3 h-3" fill={isPinned ? "currentColor" : "none"} />
                              </button>
                              <span className="text-[10px] font-semibold text-[#1A1B1A] flex-1 truncate">{n.appointment_type || "Note"}</span>
                              {isDraft ? (
                                <button onClick={e => { e.stopPropagation(); loadDraft(n); }} data-testid={`draft-edit-${n.id}`}
                                  className="text-[8px] font-bold px-1.5 py-0.5 rounded-full bg-[#D4A843]/15 text-[#8B6914] border border-[#D4A843]/25 hover:bg-[#D4A843]/25 transition-colors cursor-pointer">DRAFT</button>
                              ) : (
                                <span className="text-[8px] font-semibold px-1.5 py-0.5 rounded-full bg-[#205B5A]/10 text-[#205B5A]">Final</span>
                              )}
                            </div>
                            <p className="text-[9px] text-[#757675] ml-5">{n.practitioner_name} · {fmtDate(n.created_at)}</p>
                            {isDraft && n.updated_at && n.updated_at !== n.created_at && <p className="text-[9px] text-[#D4A843] ml-5">Last updated {fmtDate(n.updated_at)}</p>}
                            {noteDisplayFields(n)[0]?.value && <p className="text-[10px] text-[#4A4B4A] mt-1 ml-5 line-clamp-2">{noteDisplayFields(n)[0].value}</p>}
                            {/* Real body chart thumbnails */}
                            {n.body_charts?.length > 0 && (
                              <div className="ml-5 mt-1.5 flex gap-1.5">
                                {n.body_charts.slice(0, 2).map((bc, ci) => (
                                  <div key={ci} className="w-10 h-12 bg-white rounded border border-[#1A1B1A]/[0.06] overflow-hidden relative">
                                    <img src={getTemplateImage(bc.templateId)} alt="" className="w-full h-full object-contain p-0.5 opacity-70" />
                                    {(bc.drawings?.length > 0 || bc.texts?.length > 0) && (
                                      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 700" preserveAspectRatio="xMidYMid meet">
                                        {bc.drawings?.map((d, di) => <polyline key={di} points={d.points?.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={d.color} strokeWidth={d.width} strokeLinecap="round" />)}
                                      </svg>
                                    )}
                                  </div>
                                ))}
                                {n.body_charts.length > 2 && <span className="text-[8px] text-[#757675] self-end">+{n.body_charts.length - 2}</span>}
                              </div>
                            )}
                          </div>
                          {isExpanded && (
                            <div className="px-3 pb-3 space-y-1.5 border-t border-[#1A1B1A]/[0.04]">
                              {noteDisplayFields(n).map(({ key, label, value }) => (
                                <div key={key} className="mt-1.5"><p className="text-[8px] font-semibold text-[#205B5A]/60 uppercase">{label}</p><p className="text-[10px] text-[#4A4B4A] leading-relaxed">{value}</p></div>
                              ))}
                              {n.body_charts?.length > 0 && (
                                <div className="mt-2"><p className="text-[8px] font-semibold text-[#205B5A]/60 uppercase mb-1">Body charts</p>
                                <div className="flex gap-2">{n.body_charts.map((bc, ci) => (
                                  <div key={ci} className="w-20 h-24 bg-white rounded-lg border border-[#205B5A]/10 overflow-hidden relative">
                                    <img src={getTemplateImage(bc.templateId)} alt={bc.templateId || ""} className="w-full h-full object-contain p-1 opacity-70" />
                                    {(bc.drawings?.length > 0 || bc.texts?.length > 0) && (
                                      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 600 700" preserveAspectRatio="xMidYMid meet">
                                        {bc.drawings?.map((d, di) => <polyline key={di} points={d.points?.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke={d.color} strokeWidth={d.width} strokeLinecap="round" />)}
                                        {bc.texts?.map((t, ti) => <text key={ti} x={t.x} y={t.y} fill={t.color} fontSize="10" fontWeight="500">{t.text}</text>)}
                                      </svg>
                                    )}
                                  </div>
                                ))}</div></div>
                              )}
                              <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#1A1B1A]/[0.04]">
                                {!isReadOnly && <button onClick={() => copyPrevious(n)} className="text-[9px] font-medium text-[#205B5A] hover:text-[#174544] flex items-center gap-0.5"><Copy className="w-2.5 h-2.5" /> Copy to current</button>}
                                <button onClick={() => exportNotePdf(n)} className="text-[9px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] flex items-center gap-0.5"><Download className="w-2.5 h-2.5" /> PDF</button>
                                <button onClick={() => openNoteEmail(n)} data-testid={`note-email-btn-${n.id}`} className="text-[9px] font-medium text-[#4A4B4A] hover:text-[#1A1B1A] flex items-center gap-0.5"><Mail className="w-2.5 h-2.5" /> Email</button>
                              </div>
                            </div>
                          )}
                        </div>
                      ); })}
                    </div>
                    {/* Pagination */}
                    {sortedPrev.length > NOTES_PER_PAGE && (
                      <div className="flex items-center justify-between mt-3 pt-2 border-t border-[#1A1B1A]/[0.04]">
                        <button onClick={() => setNotesPage(p => Math.max(1, p - 1))} disabled={notesPage === 1}
                          className="text-[9px] font-medium text-[#205B5A] disabled:text-[#B0BEC5] disabled:cursor-default">Previous</button>
                        <span className="text-[9px] text-[#757675]">{notesPage} / {Math.ceil(sortedPrev.length / NOTES_PER_PAGE)}</span>
                        <button onClick={() => setNotesPage(p => Math.min(Math.ceil(sortedPrev.length / NOTES_PER_PAGE), p + 1))} disabled={notesPage >= Math.ceil(sortedPrev.length / NOTES_PER_PAGE)}
                          className="text-[9px] font-medium text-[#205B5A] disabled:text-[#B0BEC5] disabled:cursor-default">Next</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {rightTab === "patient" && (
              <div className="space-y-3">
                <div><p className="text-xs font-medium text-[#1A1B1A]">{p?.first_name} {p?.last_name}</p><p className="text-[10px] text-[#757675]">DOB: {p?.date_of_birth || "—"} · {p?.gender || "—"}</p></div>
                {patientData?.alerts?.length > 0 && (
                  <div><p className="text-[9px] font-semibold text-red-600 uppercase mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Medical alerts</p>
                    {patientData.alerts.map(a => <div key={a.id} className="bg-red-50 rounded px-2 py-1.5 mb-1"><p className="text-[10px] font-medium text-red-700">{a.alert_type}</p><p className="text-[9px] text-red-600">{a.description}</p></div>)}
                  </div>
                )}
                {patientData?.flags?.length > 0 && <div className="flex flex-wrap gap-1">{patientData.flags.map(f => <span key={f.id} className="text-[9px] bg-[#205B5A]/10 text-[#205B5A] px-2 py-0.5 rounded-full">{f.label}</span>)}</div>}
                {p?.occupation && <p className="text-[10px] text-[#757675]">Occupation: {p.occupation}</p>}
                {p?.emergency_contact_name && <p className="text-[10px] text-[#757675]">Emergency: {p.emergency_contact_name} ({p.emergency_contact_phone})</p>}
              </div>
            )}

            {rightTab === "appointments" && (
              <div className="space-y-1.5">
                {patientData?.appointments?.slice(0, 15).map(a => (
                  <div key={a.id} className="bg-[#FAF9F6] rounded-lg p-2.5">
                    <p className="text-[10px] font-medium text-[#1A1B1A]">{a.date} {a.start_time}</p>
                    <p className="text-[9px] text-[#757675]">{a.appointment_type_name} · {a.practitioner_name} · <span className="capitalize">{a.status}</span></p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox viewer */}
      {lightboxChart && <BodyChartLightbox chart={lightboxChart} onClose={() => setLightboxChart(null)} />}

      {/* Body Chart Editor Overlay */}
      {showBodyChartEditor !== null && (
        <BodyChartEditor
          existingChart={showBodyChartEditor.chart || null}
          readOnly={isReadOnly}
          onSave={saveBodyChart}
          onClose={() => setShowBodyChartEditor(null)}
        />
      )}

      {/* Sidebar Email Modal */}
      {sidebarEmailNote && (
        <SidebarEmailModal note={sidebarEmailNote} patient={p} clinic={clinic} user={user} onClose={() => setSidebarEmailNote(null)} />
      )}
    </div>
  );
}
